import { useNavigate } from "react-router-dom";
import CoachShell from "@/pages/RehearsalRoom/CoachShell";
import { Badge } from "@/components/ui/badge";
import { useClientRecordOverview } from "@/lib/clientRecordQueries";
import { ViewAsClientButton } from "./ViewAsClientButton";
import OverviewTab from "./tabs/OverviewTab";
import PrepTab from "./tabs/PrepTab";
import CoachingReviewTab from "./tabs/CoachingReviewTab";
import TimelineTab from "./tabs/TimelineTab";
import SessionsTab from "./tabs/SessionsTab";
import PracticeLayersTab from "./tabs/PracticeLayersTab";
import PresentationsTab from "./tabs/PresentationsTab";
import PresenceTab from "./tabs/PresenceTab";
import JourneyConfigTab from "./tabs/JourneyConfigTab";
import FilesVideosTab from "./tabs/FilesVideosTab";
import MessagesTab from "./tabs/MessagesTab";
import ReviewsTab from "./tabs/ReviewsTab";
import PreviousProgrammesTab from "./tabs/PreviousProgrammesTab";
import CommercialTab from "./tabs/CommercialTab";
import AccessSettingsTab from "./tabs/AccessSettingsTab";
import RecommendationsTab from "./tabs/RecommendationsTab";
import AuditResultTab from "./tabs/AuditResultTab";

const TABS = [
  ["overview", "Overview"], ["prep", "Prep"], ["coaching-review", "Coaching Review"], ["timeline", "Timeline"],
  ["sessions", "Sessions"], ["practice-layers", "Practice & Layers"], ["recommendations", "Recommendations"],
  ["presentations", "Presentations"], ["audit-result", "Audit Result"],
  ["presence", "Presence"], ["journey", "Journey Configuration"], ["files", "Files & Videos"], ["messages", "Messages"],
  ["reviews", "Reviews & Testimonials"], ["previous-programmes", "Previous Programmes"],
  ["commercial", "Commercial/CRM"], ["access-settings", "Access & Settings"],
];

const TAB_COMPONENTS = {
  overview: OverviewTab, prep: PrepTab, "coaching-review": CoachingReviewTab, timeline: TimelineTab,
  sessions: SessionsTab, "practice-layers": PracticeLayersTab, recommendations: RecommendationsTab,
  presentations: PresentationsTab, "audit-result": AuditResultTab, presence: PresenceTab,
  journey: JourneyConfigTab, files: FilesVideosTab, messages: MessagesTab, reviews: ReviewsTab,
  "previous-programmes": PreviousProgrammesTab, commercial: CommercialTab, "access-settings": AccessSettingsTab,
};

/**
 * Master v5 Prompt 17 — the full coach Client 360 record. Every tab
 * loads its own data lazily (only the active one) via
 * clientRecordQueries.js's per-tab hooks — this shell only owns the
 * header snapshot + tab strip.
 */
export default function Client360Page({ workspaceId, memberId, tab }) {
  const navigate = useNavigate();
  const { data, isPending, isError } = useClientRecordOverview(workspaceId, memberId);
  const blockedTeamJourney = tab === "journey" && data?.access_type === "team";
  const ActiveTab = blockedTeamJourney ? OverviewTab : (TAB_COMPONENTS[tab] || OverviewTab);

  return (
    <CoachShell>
      <div data-testid="client-360-page">
        <button
          onClick={() => navigate("/rehearsal-room/coach/clients")}
          className="text-xs text-ink-soft hover:text-ink mb-3 transition-colors"
          data-testid="client-360-back-to-clients"
        >
          ← Clients
        </button>

        {isPending ? (
          <p className="text-sm text-ink-soft" data-testid="client-360-loading">Loading client record…</p>
        ) : isError ? (
          <p className="text-sm text-red-600" data-testid="client-360-error">Couldn't load this client record.</p>
        ) : (
          <>
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
              <div>
                <h1 className="font-serif text-2xl text-ink mb-1" data-testid="client-360-member-name">{data.member_name}</h1>
                <div className="flex items-center gap-2 text-sm text-ink-soft">
                  <span data-testid="client-360-workspace-title">{data.workspace_title}</span>
                  <Badge variant="outline" className="uppercase text-[10px] tracking-wide" data-testid="client-360-access-type">{data.access_type}</Badge>
                  <Badge variant="outline" className="uppercase text-[10px] tracking-wide" data-testid="client-360-status">{data.status}</Badge>
                  {data.access_type === "team" && (
                    <button
                      onClick={() => navigate(`/rehearsal-room/coach/team/${workspaceId}/overview`)}
                      className="text-terracotta hover:underline"
                      data-testid="client-360-view-team-link"
                    >
                      Member of: {data.workspace_title} → View Team
                    </button>
                  )}
                </div>
              </div>
              <ViewAsClientButton
                workspaceId={workspaceId} workspaceTitle={data.workspace_title}
                memberId={memberId} memberName={data.member_name}
              />
            </div>

            <div className="flex items-center gap-2 mb-6 flex-wrap border-b border-ink/10 pb-3 overflow-x-auto" data-testid="client-360-tabs">
              {TABS.filter(([key]) => (key !== "presence" || data.programme_type === "executive_presence") && (key !== "journey" || data.access_type !== "team")).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => navigate(`/rehearsal-room/coach/w/${workspaceId}/client/${memberId}/${key}`)}
                  className={`text-sm px-3 py-1.5 rounded-full whitespace-nowrap transition-colors ${tab === key ? "bg-ink text-paper" : "text-ink-soft hover:bg-ink/5"}`}
                  data-testid={`client-360-tab-${key}`}
                >
                  {label}
                </button>
              ))}
            </div>

            <ActiveTab workspaceId={workspaceId} memberId={memberId} overview={data} />
          </>
        )}
      </div>
    </CoachShell>
  );
}
